/**
 * Utility functions for Advanced Trip place management
 */

export type PlaceType = 'attraction' | 'food' | 'hotel' | 'experience';

export interface PlaceTypeConfig {
  code: string;
  label: string;
  color: string;
  bgColor: string;
}

/**
 * Get place type configuration
 */
export const getPlaceTypeConfig = (type: PlaceType): PlaceTypeConfig => {
  switch (type) {
    case 'attraction':
      return {
        code: 'A',
        label: 'Attraction',
        color: 'text-blue-700',
        bgColor: 'bg-blue-100',
      };
    case 'food':
      return {
        code: 'F',
        label: 'Food',
        color: 'text-orange-700',
        bgColor: 'bg-orange-100',
      };
    case 'experience':
      return {
        code: 'E',
        label: 'Experience',
        color: 'text-purple-700',
        bgColor: 'bg-purple-100',
      };
    case 'hotel':
      return {
        code: 'H',
        label: 'Hotel',
        color: 'text-green-700',
        bgColor: 'bg-green-100',
      };
    default:
      return {
        code: 'A',
        label: 'Attraction',
        color: 'text-gray-700',
        bgColor: 'bg-gray-100',
      };
  }
};

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param lat1 Latitude of first point
 * @param lon1 Longitude of first point
 * @param lat2 Latitude of second point
 * @param lon2 Longitude of second point
 * @returns Distance in kilometers
 */
export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
    Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return Math.round(distance * 10) / 10; // Round to 1 decimal place
};

const toRad = (degrees: number): number => {
  return degrees * (Math.PI / 180);
};

/**
 * Format distance for display
 */
export const formatDistance = (km: number): string => {
  if (km < 1) {
    return `${Math.round(km * 1000)}m`;
  }
  return `${Math.round(km)}km`;
};

